import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Form, Formik, type FormikHelpers } from 'formik';
import { type ReactNode } from 'react';
import { useSelector } from 'react-redux';

import FormField from 'components/common/formikForm/formField';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import SubmitButton from 'components/common/formikForm/SubmitButton';

import { PERMISSIONS_TO_ASSIGN_USER_ROLES, Role } from 'data/constants/roles';
import useCheckEmailAvailability from 'hooks/useCheckEmail';
import type { RootState } from 'redux/store';
import type { PermissionUserFormInterface } from 'types';
import {
    permissionUserFormDefaultValues,
    permissionUserFormValidationSchema,
} from 'utils/validations/permissions';

export type PermissionUserFormMode = 'create' | 'edit';

interface PermissionUserFormProps {
    mode: PermissionUserFormMode;
    initialValues?: PermissionUserFormInterface;
    loading?: boolean;
    onClose: () => void;
    onSubmit: (
        values: PermissionUserFormInterface,
        helpers: FormikHelpers<PermissionUserFormInterface>
    ) => void | Promise<void>;
}

const formatRoleLabel = (role: string) =>
    role
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
        .join(' ');

export function PermissionUserForm({
    mode,
    initialValues,
    loading = false,
    onClose,
    onSubmit,
}: PermissionUserFormProps) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const currentUserRole = useSelector((state: RootState) => state.user.entity?.role);
    const useCheckEmail = useCheckEmailAvailability();

    const roleOptions = (PERMISSIONS_TO_ASSIGN_USER_ROLES[currentUserRole as Role] || [])
        .filter((role): role is string => Boolean(role))
        .map((role) => ({
            label: formatRoleLabel(String(role)),
            value: String(role),
        }));

    const isEdit = mode === 'edit';
    const title = isEdit ? 'Edit collaborator' : 'Invite collaborator';
    const submitLabel = isEdit ? 'Save changes' : 'Send invitation';

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            fullScreen={fullScreen}
            scroll="paper"
            slotProps={{
                paper: {
                    sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: fullScreen ? '100%' : '90vh',
                        borderRadius: fullScreen ? 0 : 2,
                        m: fullScreen ? 0 : 2,
                    },
                },
            }}
        >
            <Formik
                enableReinitialize
                initialValues={initialValues || permissionUserFormDefaultValues}
                validationSchema={permissionUserFormValidationSchema(
                    useCheckEmail,
                    isEdit ? initialValues?.email : undefined
                )}
                onSubmit={onSubmit}
            >
                <Form
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                >
                    <DialogTitle
                        sx={{
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 1,
                            py: 2,
                        }}
                    >
                        <Stack spacing={0.5} sx={{ minWidth: 0, pr: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                                {title}
                            </Typography>
                            {!isEdit ? (
                                <Typography variant="body2" color="text.secondary">
                                    Invite a team member and assign their role.
                                </Typography>
                            ) : null}
                        </Stack>
                    </DialogTitle>

                    <DialogContent dividers sx={{ flex: '1 1 auto', overflowY: 'auto' }}>
                        <BoxSection title="Personal info">
                            <FormGridLayout>
                                <FormField
                                    name="firstname"
                                    label="First name"
                                    required
                                    disabled={loading}
                                />
                                <FormField
                                    name="lastname"
                                    label="Last name"
                                    required
                                    disabled={loading}
                                />
                                <FormField
                                    name="middlename"
                                    label="Middle name"
                                    disabled={loading}
                                />
                                <FormField
                                    name="role"
                                    label="Role"
                                    type="select"
                                    required
                                    disabled={loading}
                                    options={roleOptions}
                                />
                                {isEdit ? (
                                    <FormField
                                        name="email"
                                        label="Email"
                                        type="email"
                                        required
                                        disabled={loading}
                                    />
                                ) : null}
                            </FormGridLayout>
                        </BoxSection>

                        <BoxSection title="Contact details">
                            <FormGridLayout>
                                {!isEdit ? (
                                    <FormField
                                        name="email"
                                        label="Email"
                                        type="email"
                                        required
                                        disabled={loading}
                                    />
                                ) : null}
                                <FormField
                                    name="phone_number"
                                    label="Phone number"
                                    type="phone"
                                    disabled={loading}
                                />
                            </FormGridLayout>
                        </BoxSection>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            flexShrink: 0,
                            px: { xs: 2, sm: 3 },
                            py: 1.5,
                            gap: 1,
                            flexDirection: { xs: 'column-reverse', sm: 'row' },
                            '& > :not(style)': {
                                width: { xs: '100%', sm: 'auto' },
                                m: '0 !important',
                            },
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            disabled={loading}
                            sx={{ textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <SubmitButton loading={loading} sx={{ textTransform: 'none' }}>
                            {submitLabel}
                        </SubmitButton>
                    </DialogActions>
                </Form>
            </Formik>
        </Dialog>
    );
}

function BoxSection({ title, children }: { title: string; children: ReactNode }) {
    return (
        <Stack spacing={1.25}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {title}
            </Typography>
            {children}
        </Stack>
    );
}

export default PermissionUserForm;
